"use client";

import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import dynamic from "next/dynamic";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { publicEnv } from "@/lib/public-env";
import {
  clearGlyphWalletReturn,
  rememberGlyphWalletReturn,
  readGlyphWalletReturn,
  useGlyphWalletReturn,
} from "@/lib/wallet/glyph-wallet-return";
import { useWallet } from "@/hooks/use-wallet";
import { postLink } from "@/lib/wallet/glyph-account-requests";
import {
  GlyphAccountContext,
  privyClientTypeOf,
} from "@/lib/wallet/glyph-account";
import { GlyphSessionBoundary } from "@/lib/wallet/GlyphSessionBoundary";
import { useGlyphSessionLifecycle } from "@/lib/wallet/use-glyph-session-lifecycle";
import { useGlyphRankedSession } from "@/lib/wallet/use-glyph-ranked-session";
import { useAddGlyphWallet } from "@/lib/wallet/use-add-glyph-wallet";
import { isPrivyConfigured } from "@/lib/wallet/config";

import type {
  GlyphAccount,
  GlyphLink,
  GlyphLoginMode,
} from "@/lib/wallet/glyph-account";
import type { ReactNode } from "react";

// Privy is a large bundle; a holder who never asks for Glyph never loads it.
const GlyphSession = dynamic(() => import("@/lib/wallet/GlyphSession"), {
  ssr: false,
});

function message(error: unknown): string | null {
  return error instanceof Error
    ? error.message
    : error
      ? "Glyph failed."
      : null;
}

function GlyphAccountState({ children }: { children: ReactNode }) {
  const {
    solanaAddress,
    solana,
    proveWallet,
    claimGameIdentity,
    solanaWallet,
    connected,
  } = useWallet();
  const clientType = privyClientTypeOf(solanaWallet);
  const client = useQueryClient();
  const lifecycle = useGlyphSessionLifecycle();
  const { session, signedOut, publishSession, sessionEpoch } = lifecycle;
  const binding = useRef({
    session,
    signedOut,
    sessionEpoch,
    solanaAddress,
    proveWallet,
    solana,
    clientType,
    active: false,
  });
  useLayoutEffect(() => {
    const current = {
      session,
      signedOut,
      sessionEpoch,
      solanaAddress,
      proveWallet,
      solana,
      clientType,
      active: true,
    };
    binding.current = current;
    return () => {
      current.active = false;
    };
  }, [
    session,
    signedOut,
    sessionEpoch,
    solanaAddress,
    proveWallet,
    solana,
    clientType,
  ]);
  const currentBinding = () => {
    const expected = binding.current;
    return () =>
      expected.active &&
      binding.current === expected &&
      expected.session === session &&
      !expected.signedOut &&
      expected.sessionEpoch === sessionEpoch &&
      expected.solanaAddress === solanaAddress &&
      expected.proveWallet === proveWallet &&
      expected.solana === solana &&
      expected.clientType === clientType;
  };
  const [identityUnavailable, setIdentityUnavailable] = useState(false);
  const walletReturn = useGlyphWalletReturn();
  const expectedAddress = solanaAddress ?? walletReturn?.address;
  const requestSignIn = useCallback(
    (mode: GlyphLoginMode) => {
      setIdentityUnavailable(false);
      if (connected && solanaAddress)
        rememberGlyphWalletReturn(solanaWallet ?? "phantom", solanaAddress);
      else clearGlyphWalletReturn();
      lifecycle.signIn(mode);
    },
    [lifecycle, solanaAddress, solanaWallet, connected]
  );

  const signIn = useCallback(() => requestSignIn("oauth"), [requestSignIn]);
  const signInWithEvm = useCallback(
    () => requestSignIn("existing-evm"),
    [requestSignIn]
  );

  const linkKey = [
    "glyph-link",
    session?.privyUserId,
    expectedAddress,
    walletReturn?.ticket,
  ];
  const link = useQuery({
    queryKey: linkKey,
    queryFn: async (): Promise<GlyphLink> => {
      const isCurrent = currentBinding();
      const marker = !solanaAddress ? walletReturn : null;
      if (
        !session ||
        !expectedAddress ||
        (marker && readGlyphWalletReturn()?.ticket !== marker.ticket)
      )
        return { state: "not-proven" };
      const result = await postLink(
        session,
        expectedAddress,
        false,
        () =>
          isCurrent() &&
          (!marker || readGlyphWalletReturn()?.ticket === marker.ticket)
      );
      return marker && readGlyphWalletReturn()?.ticket !== marker.ticket
        ? { state: "not-proven" }
        : result;
    },
    enabled: Boolean(session?.authenticated && expectedAddress),
    staleTime: 60_000,
    // The wallet is proven elsewhere (connecting, the game, the claim page),
    // so an unproven answer is asked again until it is.
    refetchInterval: (query) =>
      query.state.data?.state === "not-proven" ? 15_000 : false,
    retry: 1,
  });
  const ranked = useGlyphRankedSession(
    session,
    signedOut,
    sessionEpoch,
    solanaAddress
  );
  const named = ranked.named;
  const relink = useMutation({
    mutationFn: async (replace: boolean) => {
      const isCurrent = currentBinding();
      if (!session || !solanaAddress || !isCurrent()) return;
      // Reuse a matching server proof; moving a link still requires fresh proof.
      const prove = replace ? proveWallet : (claimGameIdentity ?? proveWallet);
      if (!prove) return;
      const proven = await prove();
      if (!isCurrent()) return;
      if (!proven) throw new Error("The wallet did not sign.");
      const result = await postLink(session, solanaAddress, replace, isCurrent);
      if (!isCurrent()) return;
      client.setQueryData(linkKey, result);
    },
  });
  const addToGlyph = useAddGlyphWallet({
    session,
    address: solanaAddress,
    solana,
    clientType,
    enabled: !signedOut && session?.authenticated === true,
    currentBinding,
  });

  const signOut = useCallback(() => {
    binding.current.active = false;
    clearGlyphWalletReturn();
    void ranked.revoke().catch(() => undefined);
    lifecycle.signOut();
    void client.cancelQueries({ queryKey: ["glyph-link"] });
    void client.cancelQueries({ queryKey: ["glyph-seat"] });
    client.removeQueries({ queryKey: ["glyph-link"] });
    client.removeQueries({ queryKey: ["glyph-seat"] });
    relink.reset();
    addToGlyph.reset();
  }, [addToGlyph, client, relink, lifecycle, ranked]);

  const status: GlyphAccount["status"] = signedOut
    ? "signed-out"
    : session?.ready
      ? session.authenticated
        ? "signed-in"
        : "signed-out"
      : lifecycle.starting
        ? "starting"
        : "signed-out";
  const value = useMemo(
    () => ({
      status,
      accountId: signedOut ? null : session?.privyUserId,
      signIn,
      signInWithEvm,
      signOut,
      link: signedOut ? undefined : link.data,
      linkError:
        (identityUnavailable
          ? "Glyph is unavailable. Try signing in again."
          : null) ??
        message(relink.error) ??
        message(addToGlyph.error) ??
        message(link.error),
      linking: link.isFetching || relink.isPending,
      relink: () => relink.mutate(true),
      proveAndLink: () => relink.mutate(false),
      addToGlyph: addToGlyph.add,
      addingToGlyph: addToGlyph.pending,
      addedToGlyph: addToGlyph.added,
      addedToGlyphPending: addToGlyph.membershipLoading,
      named: !signedOut && named,
      rankedWallet: signedOut ? null : ranked.rankedWallet,
      rankingPending: !signedOut && ranked.pending,
    }),
    [
      status,
      signIn,
      signInWithEvm,
      signOut,
      link,
      relink,
      addToGlyph,
      named,
      ranked.rankedWallet,
      ranked.pending,
      signedOut,
      identityUnavailable,
      session?.privyUserId,
    ]
  );
  return (
    <GlyphAccountContext value={value}>
      {children}
      {lifecycle.shouldMount && (
        <GlyphSessionBoundary
          key={sessionEpoch}
          onFailure={() => {
            setIdentityUnavailable(true);
            signOut();
          }}
        >
          <GlyphSession onSession={publishSession} />
        </GlyphSessionBoundary>
      )}
    </GlyphAccountContext>
  );
}

/**
 * Sign in with Glyph is on off Ethereum mainnet, and on mainnet only once
 * NEXT_PUBLIC_MUDWATER_GLYPH_SIGNIN is "on"; "off" turns it off anywhere.
 * Glyph's Privy apps allow our origins via our Privy client id (2026-10-18);
 * the flow is verified live on staging before production shows it.
 */
const glyphSignInEnabled = () => {
  const flag = publicEnv("NEXT_PUBLIC_MUDWATER_GLYPH_SIGNIN");
  const mainnet = publicEnv("NEXT_PUBLIC_MUDWATER_EVM_CHAIN_ID") === "1";
  return isPrivyConfigured && (flag === "on" || (flag !== "off" && !mainnet));
};

/**
 * Sign in with Glyph beside Phantom. Must sit inside the wallet bridge, which
 * it reads and never writes: Glyph names the holder, Phantom holds the cards.
 * Privy is mounted beside the app, not around it, so loading it later never
 * remounts the page.
 */
export function GlyphAccountProvider({ children }: { children: ReactNode }) {
  return glyphSignInEnabled() ? (
    <GlyphAccountState>{children}</GlyphAccountState>
  ) : (
    children
  );
}
